import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Activity } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const PAIN_ZONES = [
  "L_TMJ", "R_TMJ",
  "L_masseter", "R_masseter",
  "L_temporalis", "R_temporalis",
  "L_pterygoid", "R_pterygoid",
  "L_SCM", "R_SCM",
];
const MUSCLES = [
  "masseter_R_superficial", "masseter_R_deep", "masseter_L_superficial", "masseter_L_deep",
  "temporalis_R_anterior", "temporalis_R_middle", "temporalis_R_posterior",
  "temporalis_L_anterior", "temporalis_L_middle", "temporalis_L_posterior",
  "SCM_R", "SCM_L", "trapezius_R", "trapezius_L",
  "pterygoid_lateral_R", "pterygoid_lateral_L", "pterygoid_medial_R", "pterygoid_medial_L",
];
const JOINT_SITES = ["lateral_pole_R", "lateral_pole_L", "posterior_attachment_R", "posterior_attachment_L"];

export default function TmdAssessment() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    patient_id: "",
    pain_intensity_vas: 5,
    pain_duration_weeks: "",
    pain_pattern: "episodic",
    pain_locations: [],
    headache_frequency_per_week: "",
    jaw_locking_history: false,
    joint_sounds_right: "none",
    joint_sounds_left: "none",
    mouth_opening_mm: 40,
    deviation_on_opening: "none",
    lateral_excursion_right_mm: 8,
    lateral_excursion_left_mm: 8,
    muscle_palpation: {},
    joint_palpation: {},
    depression_screen_pos: false,
    anxiety_screen_pos: false,
    clinician_notes: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const tog = (z) => setF((x) => ({
    ...x, pain_locations: x.pain_locations.includes(z) ? x.pain_locations.filter(p=>p!==z) : [...x.pain_locations, z]
  }));
  const score = (key, type) => (val) => setF((x) => ({ ...x, [type]: { ...x[type], [key]: val } }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        patient_id: f.patient_id || null,
        pain_duration_weeks: f.pain_duration_weeks ? parseInt(f.pain_duration_weeks) : null,
        headache_frequency_per_week: f.headache_frequency_per_week ? parseInt(f.headache_frequency_per_week) : null,
      };
      const r = await api.post("/tmd-assessment/analyze", payload);
      toast.success("TMD analysis complete");
      nav(`/tmd-assessment/${r.data.id}`);
    } catch (err) {
      toast.error(errMsg(err, "TMD analysis failed"));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="kicker">Clinical Module · DC/TMD</div>
      <h1 data-testid="tmd-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1">TMD Assessment</h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
        Validated DC/TMD screening + functional + palpation findings. AI returns Axis I / Axis II
        diagnosis, severity, imaging recommendation, splint type, referrals, and CDT codes.
      </p>

      <div className="mt-7 grid md:grid-cols-2 gap-6">
        <Field label="Patient ID (optional)">
          <input value={f.patient_id} onChange={(e)=>set("patient_id", e.target.value)} data-testid="tmd-patient-id" placeholder="UUID from /patients" className={iCl} />
        </Field>
        <Field label={`Pain intensity — VAS ${f.pain_intensity_vas}/10`}>
          <input type="range" min={0} max={10} value={f.pain_intensity_vas} onChange={(e)=>set("pain_intensity_vas", parseInt(e.target.value))} data-testid="tmd-vas" className="w-full accent-[var(--pa-gold)]" />
        </Field>
        <Field label="Pain duration (weeks)"><input type="number" value={f.pain_duration_weeks} onChange={(e)=>set("pain_duration_weeks", e.target.value)} className={iCl} /></Field>
        <Field label="Pain pattern">
          <select value={f.pain_pattern} onChange={(e)=>set("pain_pattern", e.target.value)} className={iCl} data-testid="tmd-pattern">
            <option value="episodic">Episodic</option><option value="chronic">Chronic</option><option value="constant">Constant</option>
          </select>
        </Field>
        <Field label="Headache freq (per week)"><input type="number" value={f.headache_frequency_per_week} onChange={(e)=>set("headache_frequency_per_week", e.target.value)} className={iCl} /></Field>
        <Field label="Jaw locking history">
          <label className="flex items-center gap-2 text-sm text-[var(--pa-text)]"><input type="checkbox" checked={f.jaw_locking_history} onChange={(e)=>set("jaw_locking_history", e.target.checked)} className="accent-[var(--pa-gold)]" /> Yes</label>
        </Field>
        <Field label="Joint sounds R">
          <select value={f.joint_sounds_right} onChange={(e)=>set("joint_sounds_right", e.target.value)} className={iCl}>
            {["none","click_opening","click_closing","reciprocal","crepitus"].map(o=><option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Joint sounds L">
          <select value={f.joint_sounds_left} onChange={(e)=>set("joint_sounds_left", e.target.value)} className={iCl}>
            {["none","click_opening","click_closing","reciprocal","crepitus"].map(o=><option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={`Max opening: ${f.mouth_opening_mm} mm`}><input type="range" min={10} max={60} value={f.mouth_opening_mm} onChange={(e)=>set("mouth_opening_mm", parseInt(e.target.value))} className="w-full accent-[var(--pa-gold)]" /></Field>
        <Field label="Deviation on opening">
          <select value={f.deviation_on_opening} onChange={(e)=>set("deviation_on_opening", e.target.value)} className={iCl}>
            <option value="none">None</option><option value="left">Left</option><option value="right">Right</option>
          </select>
        </Field>
        <Field label={`Lateral excursion R: ${f.lateral_excursion_right_mm} mm`}><input type="range" min={0} max={15} value={f.lateral_excursion_right_mm} onChange={(e)=>set("lateral_excursion_right_mm", parseInt(e.target.value))} className="w-full accent-[var(--pa-gold)]" /></Field>
        <Field label={`Lateral excursion L: ${f.lateral_excursion_left_mm} mm`}><input type="range" min={0} max={15} value={f.lateral_excursion_left_mm} onChange={(e)=>set("lateral_excursion_left_mm", parseInt(e.target.value))} className="w-full accent-[var(--pa-gold)]" /></Field>
      </div>

      <Section title="Pain map (click to toggle)">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tmd-pain-map">
          {PAIN_ZONES.map((z) => (
            <button key={z} type="button" onClick={()=>tog(z)} data-testid={`tmd-zone-${z}`}
              className={`text-[11px] px-2 py-1.5 rounded border ${f.pain_locations.includes(z)
                ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] border-[var(--pa-gold)] font-semibold"
                : "bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)] border-[var(--pa-border)]"}`}>{z.replace(/_/g," ")}</button>
          ))}
        </div>
      </Section>

      <Section title="Muscle palpation (0-3)">
        <ScoreGrid items={MUSCLES} values={f.muscle_palpation} setter={(k,v)=>score(k,"muscle_palpation")(v)} />
      </Section>
      <Section title="Joint palpation (0-3)">
        <ScoreGrid items={JOINT_SITES} values={f.joint_palpation} setter={(k,v)=>score(k,"joint_palpation")(v)} />
      </Section>

      <Section title="Axis II screen">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.depression_screen_pos} onChange={(e)=>set("depression_screen_pos", e.target.checked)} className="accent-[var(--pa-gold)]" /> Depression screen positive</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.anxiety_screen_pos} onChange={(e)=>set("anxiety_screen_pos", e.target.checked)} className="accent-[var(--pa-gold)]" /> Anxiety screen positive</label>
        </div>
      </Section>

      <textarea value={f.clinician_notes} onChange={(e)=>set("clinician_notes", e.target.value)} rows={3} placeholder="Clinician notes" className={`${iCl} mt-6 w-full`} data-testid="tmd-notes" />

      <button onClick={submit} disabled={busy} data-testid="tmd-submit"
        className="mt-6 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> : <><Sparkles size={14} /> Run TMD analysis</>}
      </button>
    </Layout>
  );
}

const iCl = "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5";

function Field({ label, children }) {
  return <div><label className="kicker block mb-1">{label}</label>{children}</div>;
}
function Section({ title, children }) {
  return <section className="mt-7"><div className="kicker mb-3">{title}</div>{children}</section>;
}
function ScoreGrid({ items, values, setter }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {items.map((it) => (
        <div key={it} className="flex items-center gap-2">
          <span className="flex-1 text-xs text-[var(--pa-text-muted)] truncate">{it.replace(/_/g," ")}</span>
          {[0,1,2,3].map((n) => (
            <button key={n} type="button" onClick={()=>setter(it, n)} data-testid={`palp-${it}-${n}`}
              className={`w-7 h-7 text-[11px] rounded border ${values[it]===n
                ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] border-[var(--pa-gold)] font-semibold"
                : "bg-[var(--pa-bg-2)] text-[var(--pa-text-dim)] border-[var(--pa-border)] hover:text-[var(--pa-text)]"}`}>{n}</button>
          ))}
        </div>
      ))}
    </div>
  );
}
